import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormGroup,  ReactiveFormsModule,  } from '@angular/forms';
import { DailyDosingService } from '../../../../services/daily-dosing.service';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DynamicField } from '../../../../interfaces/dynamic-form-model';
import { DynamicFormBuilderService } from '../../../../services/dynamic-form-builder.service';
import { DynamicFormRuleApplierService } from '../../../../services/dynamic-form-builder-rule-applier.service';
import { NotificationService } from '../../../../services/notificationservice';
import { formatDateTime, handleDateKeydown } from '../../../shared/commonutils/helper';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { DatetimePickerInputComponent } from '../../../shared/components/datetime-picker-input/datetime-picker-input.component';

@Component({
  selector: 'app-add-edit-daily-dosing',
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    DragDropModule,
    DatetimePickerInputComponent
  ],
  templateUrl: './add-edit-daily-dosing.component.html',
  styleUrl: './add-edit-daily-dosing.component.scss'
})
export class AddEditDailyDosingComponent implements OnInit {
  canSave: boolean = false;
  form!: FormGroup;
  fields!: DynamicField[];
  private destroyRef = inject(DestroyRef);
  displayValue = signal<string>('');
  uiState: any = {};
  mode: string = '';
  headerLabel: string = '';
  onflyGridData: any[] = [];

  constructor(
    private _dialogRef: MatDialogRef<AddEditDailyDosingComponent>,
    @Inject(MAT_DIALOG_DATA) public _data: any,
    private _dailyDosingService: DailyDosingService,
    private _fb: DynamicFormBuilderService,
    private _ruleApplier: DynamicFormRuleApplierService,
    private _notificationService: NotificationService,
  ) {
    this.mode = this._data.mode;
    this.canSave = this._data.canSave;
    this.getHeaderLabel();
  }

  ngOnInit(): void {

    //Sort the columns order
    var sortColumns = [...this._data.columns].sort((a, b) => a.order - b.order);

    console.log('Sorted Columns:', sortColumns);
    const rawDate = this._data.apiResponse?.Datecaptured;

    let dateValue: Date;
    if (this.mode === 'edit') {
      dateValue = rawDate instanceof Date ? rawDate : new Date(rawDate);
    } else {
      // Add and Duplicate both use current system time
      dateValue = new Date();
    }
    //Apply the rules
    this.uiState = this._ruleApplier.applyRulesForUpsertForm(sortColumns, this._data.apiResponse);
    console.log(this.uiState);
    //Set the rules result
    sortColumns.forEach(element => {
      for (const prop in this.uiState) {
        if (prop === element.key) {
          element.visible = this.uiState[element.key].visible;
          element.readonly = this.uiState[element.key].readonly;
          element.required = this.uiState[element.key].required;
          element.label = this.uiState[element.key].label;
        }
      }
    });

    //Filter the visible fields
    this.fields = sortColumns.filter(x => x.visible == true);

    console.log("fields:", this.fields);

    //Build the dynamic form
    this.form = this._fb.buildForm(this.fields);

    //Patch the values
    this._fb.patchForm(this.form, this._data.apiResponse);

    if (!isNaN(dateValue.getTime())) {
      this.form.get('Datecaptured')?.setValue(dateValue, { emitEvent: false });
    }

    //Apply readonly after rule
    this._fb.applyReadonly(this.form,this.fields);

    if (this.mode === 'scratch-pad-add' || this.mode === 'scratch-pad-edit') {
      this.onflyGridData = this._data.gridData;
    }
  }

  numberOnly(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter', "-"
    ];
    // Allow digits 0-9
    if ((event.key >= '0' && event.key <= '9') || allowedKeys.includes(event.key)) {
      return; // allow
    }
    // Optional: allow dot (.) for decimals
    if (event.key === '.') {
      return;
    }
    // Block everything else
    event.preventDefault();
  }

  onKeydown(event: KeyboardEvent) {
    handleDateKeydown(event);
  }

  getHeaderLabel(): string {

    if (this.mode === 'add') {
      this.headerLabel = "Add Daily Dosing Inputs";
    }
    else if (this.mode === 'edit') {
      this.headerLabel = "Edit Daily Dosing Inputs";
    }
    else if (this.mode === 'scratch-pad-add') {
      this.headerLabel = "Scratch Pad -Add Daily Dosing Inputs";
    }
    else if (this.mode === 'scratch-pad-edit') {
      this.headerLabel = "Scratch Pad -Edit Daily Dosing Inputs";
    }
    return '';
  }
  
  isScratchPadMode(): boolean {
    return this.mode === 'scratch-pad-add' || this.mode === 'scratch-pad-edit';
  }
  isActualAddEditMode(): boolean {
    return this.mode === 'add' || this.mode === 'edit';
  }


  //Cancel dialog
  onCancel(): void {
    this._dialogRef.close(false);
  }

  onSave(): void {
    console.log(this.form.getRawValue());

    if (!this.form || this.form.invalid) {
      this.form?.markAllAsTouched();
      return;
    }
    const numericFields: (keyof typeof raw)[] = [
      'Height', 'Volume', 'Loads', 'Other', 'Heifer_Est',
      'Dosed', 'Dosing24Hr',
      'SetPoint_RecircOntime', 'SetPoint_PumpOntime',
      'SetPoint_Cycletime', 'SetPoint_GallonsPerDay'
    ];

    const raw = this.form.getRawValue();
    const originalDate = new Date(raw.Datecaptured);
    const year = originalDate.getFullYear();
    const month = String(originalDate.getMonth() + 1).padStart(2, '0');
    const day = String(originalDate.getDate()).padStart(2, '0');
    raw.Datecaptured = formatDateTime(originalDate);

    numericFields.forEach(field =>{
      let val = raw[field];
      if(val === null || val === undefined){
        raw[field] = null;
        return;
      }
      let str = val.toString().trim();
      if(str === '' || str === '-'){
        raw[field] = null;
        return;
      }
      let parsed = Number(str);
      raw[field] = isNaN(parsed)? null : parsed;
    });

    console.log("Raw Data:", raw);
    //Record Saved to actual table
    if (this.isActualAddEditMode()) {

      this._dailyDosingService.saveDailyDosing(raw).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (response: boolean) => {
          this._notificationService.show('Daily Dosing data saved successfully', 'success');

          this._dialogRef.close(true);
        }
      })
    }

    //Record not saved in actual table, for scratch pad calucations.
    else if (this.isScratchPadMode()) {
      if (this.mode === 'scratch-pad-add') {
        this.onflyGridData.push(raw);
      }
      else {
        // Skip the existing record
        this.onflyGridData = this.onflyGridData.filter(x => x.ID != raw.ID);
        this.onflyGridData.push(raw);
      }

      var payload = {
        dosingInputs: this.onflyGridData,
        SiteID: this._data.siteID
      }
      this._dailyDosingService.getWeightedAverageForScratchPad(payload)
        .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (response: any[]) => {
            this.onflyGridData = response;
            var refreshData = {
              mode: 'scratch-pad',
              Data: this.onflyGridData,
              Status: response
            }
            this._dialogRef.close(this.onflyGridData);
          }
        })
    }
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, type: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    let isNegetive = false;

    if((type ==='Transfer Out' || type ==='Other' || type ==='Loads') && val.startsWith('-')){
      isNegetive = true;
      val = val.substring(1);
    }
    if(type ==='Pump on Time(sec)' || (type ==='Transfer Out' || type ==='Other')){
      maxDecimals = 0
    }

    if (maxDecimals === 0) {
      val = val.replace(/[^0-9]/g, '');
    } else {
      val = val.replace(/[^0-9.]/g, '');
    }
    const points = val.split('.');
    if (points.length > 2) {
      val = points[0] + '.' + points.slice(1).join('');
    }
    
    const maxIntegerLength = maxValue.toString().replace('-', '').length;
    const parts = val.split('.');
    let intPart = parts[0];
    let decPart = parts.length > 1 ? parts[1] : null;

    if (intPart.length > maxIntegerLength) {
      intPart = intPart.substring(0, maxIntegerLength);
      val = decPart !== null ? `${intPart}.${decPart}` : intPart;
    }
    
    if (decPart !== null && decPart.length > maxDecimals) {
      decPart = decPart.substring(0, maxDecimals);
      val = `${intPart}.${decPart}`;
    }
    
    if(isNegetive){
      val = '-'+val;
    }
    this.displayValue.set(val);
    
    input.value = val;
    const isValueEmpty = val === '' || val === '.' || val === '-';
    const parsedValue = maxDecimals === 0 ? parseInt(val, 10) : parseFloat(val);
    const finalValue = isValueEmpty ? null : parsedValue;
    this.form.get(type)?.setValue(finalValue);
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
        input.value = val;
      }
    });
  }
}
